import {useIntl} from 'react-intl';
import React, {useMemo} from 'react';
import useSWR, {useSWRConfig} from 'swr';
import {useHistory, useLocation, useParams} from 'react-router';

import {generateUniqueString} from '../../../utils';
import {PreProcessingForm} from './PreProcessingForm';
import {toast} from '../../../base-components/StudioToast';
import {preProcessorGet} from '../../../api/preprocessor/PreProcessorGet';
import {preProcessorUpdate} from '../../../api/preprocessor/PreProcessorUpdate';
import {UpdatePreProcessorRequest} from '../../../types/preprocessor/UpdatePreProcessorRequest';

type ReturnToOptions = 'spaDetails' | undefined;

export const EditPreProcessor = () => {
  const {projectId, preProcessorId} = useParams<{
    projectId: string;
    preProcessorId: string;
  }>();
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location]);
  const spaId = search.get('spaId');
  const returnTo = search.get('returnTo') as ReturnToOptions;
  const viewOnly = search.has('viewOnly');
  const applicationId = search.get('applicationId');
  const {mutate} = useSWRConfig();
  const cacheKey = useMemo(() => generateUniqueString(), []);
  const {data: preProcessorGetResult} = useSWR(
    cacheKey,
    () => preProcessorGet({projectId, preProcessorId}),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const filesNames = useMemo(() => preProcessorGetResult?.body?.filePaths ?? [], [
    preProcessorGetResult,
  ]);

  const intl = useIntl();
  const history = useHistory();

  const getReturnText = useMemo(() => {
    switch (returnTo) {
      case 'spaDetails':
        return intl.formatMessage({id: 'form.returnToDetails'});
      default:
        return undefined;
    }
  }, [returnTo, intl]);

  const returnToPreviousPage = () => {
    if (returnTo === 'spaDetails' && applicationId) {
      history.push(
        `/spa/project/${projectId}/application/${applicationId}/inference/${spaId}/details`
      );
    } else {
      history.push(`/project/${projectId}/upload`);
    }
  };

  const handleSubmit = async (values: UpdatePreProcessorRequest) => {
    if (JSON.stringify(filesNames) !== JSON.stringify(Object.keys(values.filePaths))) {
      mutate((key: string) => key === cacheKey);
    }

    try {
      const resp = await preProcessorUpdate(values);

      if (!resp.errors?.length) {
        toast.success(intl.formatMessage({id: 'editPreProcessor.success'}));
        history.push(`/project/${projectId}/upload`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <PreProcessingForm
      viewOnly={viewOnly}
      projectId={projectId}
      fileNames={filesNames}
      onSubmit={handleSubmit}
      returnText={getReturnText}
      onCancel={returnToPreviousPage}
      template={preProcessorGetResult?.body}
      title={intl.formatMessage({id: 'editPreProcessor.header'})}
    />
  );
};
